import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Button, Card, Page, Switcher, Thumb, Tile, Tiles } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Empty, Loading, OfflineBanner } from '../components/states.tsx';
import { api, query } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useCan } from '../app/me.tsx';

/**
 * Screen 13: everything that needs a person, in one list.
 *
 * The tiles are counted from the items by the server, which is why the total
 * can never disagree with them — that is the screen's acceptance check, and it
 * is kept true in one place rather than by two queries agreeing.
 *
 * "Not now" hides one item for one person. It is a preference about a list, not
 * history, so it expires by itself rather than being deleted.
 */

interface InboxItem {
  kind: string;
  key: string;
  says: string;
  waiting_on: string | null;
  action: string;
  href: string;
  at: string | null;
  thumbnail?: string | null;
}

interface Inbox {
  items: InboxItem[];
  tiles: Record<string, number>;
  total: number;
  labels: Record<string, { tile: string; action: string }>;
  snoozed: number;
}

export function Inbox({ projectId, onGo }: { projectId: string; onGo: (to: string) => void }) {
  const can = useCan();
  const [mine, setMine] = useState<'everyone' | 'mine'>('everyone');
  const [kind, setKind] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const { state, reload } = useLoad<Inbox>(
    () => api.get(`/api/inbox${query({ project_id: projectId, mine: mine === 'mine' })}`),
    [projectId, mine]
  );
  const inbox = state.data;
  const items = (inbox?.items ?? []).filter((item) => !kind || item.kind === kind);

  /**
   * Each item's one action. The href the server sent is the route that does it,
   * so the screen does not have to know which endpoint belongs to which kind.
   */
  const doIt = async (item: InboxItem): Promise<void> => {
    setBusy(item.key);
    try {
      if (item.kind === 'offline_machine') {
        onGo(`/p/${projectId}/runs`);
        return;
      }
      if (item.kind === 'unmatched_clip' || item.kind === 'older_movie_clip') {
        onGo(`/p/${projectId}/edits`);
        return;
      }
      await api.post(item.href, {});
      setToast('Done.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  const notNow = async (item: InboxItem): Promise<void> => {
    try {
      await api.post('/api/inbox/not-now', { key: item.key, hours: 24 });
      setToast('Hidden from your inbox for a day. Nobody else’s changed.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That could not be hidden.');
    }
  };

  return (
    <Page
      eyebrow="MAESTRO · NEEDS YOU"
      title="Needs you"
      description="Everything in this project waiting on a person, so nobody has to go looking."
      action={
        <Switcher<'everyone' | 'mine'>
          value={mine}
          onChange={setMine}
          options={[
            { value: 'everyone', label: 'Everyone' },
            { value: 'mine', label: 'Mine' }
          ]}
        />
      }
    >
      {state.status === 'offline' && <OfflineBanner since={state.since} onRetry={reload} />}

      {inbox && (
        <Tiles>
          {Object.entries(inbox.tiles).map(([key, count]) => (
            <Tile
              key={key}
              count={count}
              label={inbox.labels[key]?.tile ?? key}
              on={kind === key}
              onClick={() => setKind(kind === key ? null : key)}
            />
          ))}
        </Tiles>
      )}

      {state.status === 'loading' && !inbox && <Loading says="Loading your inbox…" rows={5} />}
      {state.status === 'failed' && !inbox && (
        <CouldNotLoad what="your inbox" error={state.error} onRetry={reload} />
      )}

      {inbox && items.length === 0 && (
        <Empty
          title={inbox.total === 0 ? 'Nothing needs a person' : 'Nothing of that kind'}
          says={
            inbox.total === 0
              ? mine === 'mine'
                ? 'Nothing in this project is waiting on you.'
                : 'Every run finished, every VC is made, and every clip is matched.'
              : 'Pick another tile, or clear the one that is selected.'
          }
          {...(kind ? { action: <Button onClick={() => setKind(null)}>Show everything</Button> } : {})}
        />
      )}

      {items.map((item) => (
        <Card key={item.key}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control, alignItems: 'center' }}>
            {item.thumbnail !== undefined && (
              <Thumb url={item.thumbnail} alt="what this is about" size={72} />
            )}
            <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={text.small}>{item.says}</span>
              <span style={{ ...text.note, color: colour.inkMuted }}>
                {inbox?.labels[item.kind]?.tile ?? item.kind}
                {item.waiting_on ? ` · waiting on ${item.waiting_on}` : ''}
                {item.at ? ` · ${new Date(item.at).toLocaleDateString()}` : ''}
              </span>
            </div>
            {can.launch && (
              <div style={{ display: 'flex', gap: space.tight }}>
                <Button tone="primary" onClick={() => void doIt(item)} disabled={busy === item.key}>
                  {busy === item.key ? 'Working…' : item.action}
                </Button>
                <Button onClick={() => void notNow(item)}>Not now</Button>
              </div>
            )}
          </div>
        </Card>
      ))}

      {inbox && inbox.snoozed > 0 && (
        <span style={{ ...text.note, color: colour.inkMuted }}>
          {inbox.snoozed} {inbox.snoozed === 1 ? 'item is' : 'items are'} hidden by a "Not now" of
          yours. They come back on their own.
        </span>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
